/**
 * RealtimeProvider — one Web PubSub connection per signed-in session.
 *
 * Flow (see frontend_documentation/realtime-web-pubsub.md §5):
 *   1. GET /realtime/negotiate (Bearer JWT, via dsHttp) → short-lived wss URL.
 *   2. Open the WebSocket to Web PubSub; the SDK auto-reconnects + re-negotiates.
 *   3. `server-message` (send_to_user) and `group-message` (deal:{id} viewers)
 *      carry the §9 notification envelope → push to the inbox + toast + refetch.
 *
 * Graceful degradation: while the backend hasn't provisioned Web PubSub yet,
 * `/realtime/negotiate` returns 503 `realtime_not_configured`. We detect that,
 * skip the socket entirely, and fall back to polling the inbox — the bell still
 * works; only the *instant* push is dark.
 *
 * SUPERVISED CONNECT (why this isn't just `client.start()`):
 * a connect cycle can die for reasons the Web PubSub SDK can't recover from on
 * its own — a few seconds of backend 5xx on `/negotiate`, a laptop sleep, a VPN
 * flip, the tab being backgrounded. Previously any such failure disabled realtime
 * *permanently* for the life of the page: the socket never came back and the bell
 * sat on "Reconnecting to live updates…" forever while nothing was reconnecting.
 * So this provider owns a supervisor loop:
 *   - failed cycle  → tear the client down, retry on exponential backoff,
 *   - `stopped`     → same (the SDK gave up),
 *   - back online / tab refocused → retry immediately, backoff reset,
 *   - watchdog      → if a `disconnected` never becomes `connected`, force a
 *                     fresh cycle rather than trusting the SDK's inner loop.
 * The degraded status is also debounced (STATUS_GRACE_MS) so a two-second
 * reconnect never flashes a warning banner at the user.
 *
 * Mounted inside <AuthenticatedDashboard> so it always has a valid JWT and only
 * runs for authenticated users.
 */
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import { WebPubSubClient } from "@azure/web-pubsub-client";

import { useAuth } from "../auth/AuthProvider";
import { realtimeApi, isRealtimeNotConfigured } from "../api/realtimeApi";
import { useNotificationsHook } from "../hooks/useNotificationsHook";
import { useUnderwritingHook } from "../hooks/useUnderwritingHook";
import {
  notificationPrepend,
  notificationsSetRealtimeStatus,
} from "../redux/reducers/notificationsReducer";
import { loanDrilldownSet } from "../redux/actions/loanDrilldownActions";
import { PIPELINES, PIPELINE_KEYS } from "../pipelines/pipelineConfig";

// Poll cadence (ms). Slower when we have a live socket (just a backstop for the
// badge); faster when realtime is unconfigured / disconnected and polling is the
// only path that surfaces new notifications.
const POLL_CONNECTED_MS = 60_000;
const POLL_FALLBACK_MS = 30_000;

// How long a non-connected state must persist before it is surfaced in the UI.
// The SDK recovers most blips (sleep/wake, wifi hop) in a second or two — showing
// a banner for those is pure noise, which is what made this look broken.
const STATUS_GRACE_MS = 12_000;

// Backoff between full reconnect cycles (a cycle = negotiate + new client + start).
const RETRY_BACKOFF_MS = [5_000, 15_000, 30_000, 60_000, 120_000, 300_000];

// Web PubSub not provisioned (503): that's an infra change, not a blip — check
// back rarely instead of hammering.
const UNCONFIGURED_RETRY_MS = 600_000;

// If a `disconnected` hasn't turned back into `connected` within this window, stop
// trusting the SDK's internal reconnect loop and rebuild the client from scratch.
const WATCHDOG_MS = 45_000;

// Consecutive negotiate failures inside one cycle before we abandon the cycle.
// (The SDK re-calls getClientAccessUrl on every reconnect attempt; without a cap
// a failing backend turns into a request storm.)
const MAX_NEGOTIATE_RETRIES = 2;

// Don't let repeated focus/online events kick off cycles faster than this.
const WAKE_THROTTLE_MS = 5_000;

const RealtimeContext = createContext({
  connectionId: null,
  status: "idle",
  subscribeChat: () => () => {},
  subscribeDeal: () => () => {},
  subscribeBoard: () => () => {},
});

export const useRealtime = () => useContext(RealtimeContext);

export function RealtimeProvider({ children }) {
  const { user } = useAuth();
  const dispatch = useDispatch();
  const email = user?.email;

  const [connectionId, setConnectionId] = useState(null);
  const [status, setStatus] = useState("idle");

  // Per-loan chat subscribers: dealId → Set<handler>. Populated by useLoanChat
  // when a drilldown chat panel is open; `chat_message` events (which ride the
  // SAME socket as notifications) are fanned out to them instead of the inbox.
  const chatSubscribersRef = useRef(new Map());

  const subscribeChat = useCallback((dealId, handler) => {
    if (!dealId || typeof handler !== "function") return () => {};
    const map = chatSubscribersRef.current;
    if (!map.has(dealId)) map.set(dealId, new Set());
    map.get(dealId).add(handler);
    return () => {
      const set = map.get(dealId);
      if (!set) return;
      set.delete(handler);
      if (!set.size) map.delete(dealId);
    };
  }, []);

  // Per-loan collaboration subscribers: dealId → Set<handler>. Receives the
  // deal-group collab events that are NOT inbox notifications — `deal_updated`
  // (the "this loan changed, refresh?" nudge) and `presence_*` (who's viewing).
  const dealSubscribersRef = useRef(new Map());

  const subscribeDeal = useCallback((dealId, handler) => {
    if (!dealId || typeof handler !== "function") return () => {};
    const map = dealSubscribersRef.current;
    if (!map.has(dealId)) map.set(dealId, new Set());
    map.get(dealId).add(handler);
    return () => {
      const set = map.get(dealId);
      if (!set) return;
      set.delete(handler);
      if (!set.size) map.delete(dealId);
    };
  }, []);

  // Request Board subscribers: `request_board_updated` (a card changed — the
  // backend pushes it to everyone on the board but the actor). Not an inbox
  // notification; the open board applies it in place.
  const boardSubscribersRef = useRef(new Set());

  const subscribeBoard = useCallback((handler) => {
    if (typeof handler !== "function") return () => {};
    boardSubscribersRef.current.add(handler);
    return () => boardSubscribersRef.current.delete(handler);
  }, []);

  const { hydrateInbox, refreshUnread, toastNotification } =
    useNotificationsHook();
  // This provider is mounted ABOVE the per-route <PipelineProvider>, so
  // context here ALWAYS resolves preclose — a deal_updated for an acquisition
  // refetched GET /underwriting/loan/{id} and 404'd with "Loan is in the
  // Acquisitions pipeline". Take the pipeline off the open route instead.
  const { pathname } = useLocation();
  const openPipelineKey = pathname.startsWith(
    `${PIPELINES[PIPELINE_KEYS.ACQUISITIONS].drilldownBase}/`,
  )
    ? PIPELINE_KEYS.ACQUISITIONS
    : PIPELINE_KEYS.UNDERWRITING;
  const { getLoanById } = useUnderwritingHook({ pipelineKey: openPipelineKey });

  // The loan currently open in the drilldown — a live event for it triggers a
  // refetch (DB is the source of truth; events are invalidation signals).
  const openLoanId = useSelector((s) => s.loanDrilldown?._id);
  const openLoanIdRef = useRef(openLoanId);
  openLoanIdRef.current = openLoanId;

  // Latest helper refs so the connect effect can stay keyed only on `email`
  // (we don't want to tear down the socket every time a callback identity changes).
  const helpersRef = useRef({});
  helpersRef.current = {
    hydrateInbox,
    refreshUnread,
    toastNotification,
    getLoanById,
    dispatch,
  };

  const setRealtime = useCallback(
    (s) => {
      setStatus(s);
      dispatch(notificationsSetRealtimeStatus(s));
    },
    [dispatch],
  );

  useEffect(() => {
    if (!email) {
      setRealtime("idle");
      setConnectionId(null);
      return undefined;
    }

    let cancelled = false;
    let client = null;
    let pollTimer = null;
    let retryTimer = null;
    let graceTimer = null;
    let cycleFailures = 0; // consecutive failed cycles → backoff index
    let generation = 0; // stale-client guard: events from old cycles are ignored
    let starting = false; // a cycle is in flight
    let isConnected = false;
    let lastWakeAt = 0;
    let shown = "idle"; // what the UI is currently displaying

    // ── status display ────────────────────────────────────────────────────
    // "connected"/"idle" apply instantly; degraded states wait out the grace
    // window so short, self-healing gaps stay invisible.
    const clearGrace = () => {
      if (graceTimer) {
        clearTimeout(graceTimer);
        graceTimer = null;
      }
    };

    const show = (s) => {
      if (cancelled || shown === s) return;
      shown = s;
      setRealtime(s);
    };

    const showNow = (s) => {
      clearGrace();
      show(s);
    };

    const showDeferred = (s) => {
      if (cancelled || shown === s || graceTimer) return;
      graceTimer = setTimeout(() => {
        graceTimer = null;
        show(s);
      }, STATUS_GRACE_MS);
    };

    // ── polling fallback ──────────────────────────────────────────────────
    const startPolling = (intervalMs) => {
      if (pollTimer) clearInterval(pollTimer);
      pollTimer = setInterval(() => {
        helpersRef.current.refreshUnread();
      }, intervalMs);
    };

    // ── client lifecycle ──────────────────────────────────────────────────
    // Bumping `generation` orphans the old client's handlers, so a socket that
    // is still winding down can't drive the status of the new cycle.
    const teardownClient = () => {
      const c = client;
      client = null;
      isConnected = false;
      generation += 1;
      if (!cancelled) setConnectionId(null);
      if (!c) return;
      setTimeout(() => {
        try {
          c.stop();
        } catch {
          // ignore
        }
      }, 0);
    };

    const scheduleRetry = (ms) => {
      if (retryTimer) clearTimeout(retryTimer);
      retryTimer = setTimeout(() => {
        retryTimer = null;
        teardownClient();
        connect();
      }, ms);
    };

    // A cycle failed: show the (debounced) degraded state, poll faster, and try
    // again later. This is what makes "Reconnecting…" an honest label.
    const failCycle = (statusLabel) => {
      if (cancelled) return;
      showDeferred(statusLabel);
      startPolling(POLL_FALLBACK_MS);
      const delay =
        statusLabel === "unconfigured"
          ? UNCONFIGURED_RETRY_MS
          : RETRY_BACKOFF_MS[
              Math.min(cycleFailures, RETRY_BACKOFF_MS.length - 1)
            ];
      cycleFailures += 1;
      scheduleRetry(delay);
    };

    const handleEvent = (raw) => {
      // The SDK delivers JSON as already-parsed data; tolerate a string too.
      let ev = raw;
      if (typeof raw === "string") {
        try {
          ev = JSON.parse(raw);
        } catch {
          return;
        }
      }
      if (!ev || typeof ev !== "object") return;

      // Per-loan chat rides the same socket as notifications. Route it to the
      // open chat panel(s) and DO NOT treat it as a notification envelope.
      if (ev.type === "chat_message") {
        const subs = chatSubscribersRef.current.get(ev.dealId);
        if (subs) {
          subs.forEach((fn) => {
            try {
              fn(ev);
            } catch {
              // a misbehaving subscriber must not break the socket
            }
          });
        }
        return;
      }

      // Request Board live updates — fan out to the open board (if any) and stop.
      // Never an inbox row: the board's own alerts arrive as separate
      // `request_board.*` notifications.
      if (ev.type === "request_board_updated") {
        boardSubscribersRef.current.forEach((fn) => {
          try {
            fn(ev);
          } catch {
            // a misbehaving subscriber must not break the socket
          }
        });
        return;
      }

      // Deal-group collaboration events (NOT inbox notifications): `deal_updated`
      // (offer a non-destructive "refresh?" — never auto-refetch, the viewer may
      // have unsaved edits) and `presence_*` (who's viewing). Fan out to the open
      // loan's subscribers and stop — don't prepend to the inbox.
      if (
        ev.type === "deal_updated" ||
        String(ev.type || "").startsWith("presence_")
      ) {
        const subs = dealSubscribersRef.current.get(ev.dealId);
        if (subs) {
          subs.forEach((fn) => {
            try {
              fn(ev);
            } catch {
              // ignore a misbehaving subscriber
            }
          });
        }
        return;
      }

      const h = helpersRef.current;
      // Inbox + badge.
      h.dispatch(notificationPrepend(ev));
      // Toast only for things worth interrupting for.
      if (ev.severity === "high" || ev.severity === "med") {
        h.toastNotification(ev);
      }
      // Refetch the affected loan if it's the one on screen.
      if (ev.dealId && ev.dealId === openLoanIdRef.current) {
        h.getLoanById({
          loanId: ev.dealId,
          onSuccessFn: (data) => h.dispatch(loanDrilldownSet(data)),
        });
      }
    };

    // One connect cycle: negotiate → build client → start. Any failure hands off
    // to failCycle, which schedules the next attempt.
    const connect = async () => {
      if (cancelled || starting || client) return;
      starting = true;
      const gen = generation;
      const isStale = () => cancelled || gen !== generation;
      showDeferred("connecting");

      let firstUrl;
      try {
        const data = await realtimeApi.negotiate();
        firstUrl = data?.url;
        if (!firstUrl) throw new Error("negotiate returned no url");
      } catch (err) {
        starting = false;
        if (isStale()) return;
        failCycle(isRealtimeNotConfigured(err) ? "unconfigured" : "disconnected");
        return;
      }
      if (isStale()) {
        starting = false;
        return;
      }

      let negotiateFailures = 0;
      let cycleAbandoned = false;

      // Give up on THIS cycle (without killing realtime for good) and let the
      // supervisor schedule a fresh one on backoff.
      const abandonCycle = (statusLabel) => {
        if (cycleAbandoned || isStale()) return;
        cycleAbandoned = true;
        setTimeout(() => {
          if (isStale()) return;
          teardownClient();
          failCycle(statusLabel);
        }, 0);
      };

      const next = new WebPubSubClient({
        // Reuse the probe URL for the first connect, then re-negotiate on every
        // reconnect so the SDK always gets a fresh (un-expired) token. A failing
        // negotiate is capped so the SDK can't spin in an endless
        // reconnect→negotiate request storm; hitting the cap abandons the cycle
        // and the supervisor retries later.
        getClientAccessUrl: async () => {
          if (firstUrl) {
            const u = firstUrl;
            firstUrl = null;
            return u;
          }
          if (cycleAbandoned || isStale()) throw new Error("realtime cycle over");
          try {
            const data = await realtimeApi.negotiate();
            if (!data?.url) throw new Error("negotiate returned no url");
            negotiateFailures = 0;
            return data.url;
          } catch (err) {
            const st = err?.response?.status;
            const authFail = st === 401 || st === 403;
            const unconfigured = isRealtimeNotConfigured(err);
            negotiateFailures += 1;
            // Auth failure / not-configured won't fix on the SDK's fast retry
            // loop → end the cycle now. Transient errors get a couple of tries.
            if (
              authFail ||
              unconfigured ||
              negotiateFailures > MAX_NEGOTIATE_RETRIES
            ) {
              abandonCycle(unconfigured ? "unconfigured" : "disconnected");
            }
            throw err;
          }
        },
      });
      client = next;

      next.on("connected", (e) => {
        if (isStale()) return;
        isConnected = true;
        cycleFailures = 0;
        if (retryTimer) {
          clearTimeout(retryTimer);
          retryTimer = null;
        }
        setConnectionId(e.connectionId);
        showNow("connected");
        // Connected: live push carries the badge; poll is just a light backstop.
        startPolling(POLL_CONNECTED_MS);
      });

      next.on("disconnected", () => {
        if (isStale()) return;
        isConnected = false;
        setConnectionId(null);
        // Debounced — the SDK usually recovers before this ever paints.
        showDeferred("disconnected");
        // Socket down: poll faster since polling is the only thing surfacing
        // notifications now.
        startPolling(POLL_FALLBACK_MS);
        // Watchdog: if the SDK's own reconnect loop hasn't got us back by then,
        // rebuild from scratch. Cleared by the `connected` handler above.
        scheduleRetry(WATCHDOG_MS);
      });

      // The SDK exhausted its reconnect attempts and gave up. Nothing will ever
      // bring the socket back on its own — start a fresh cycle on backoff.
      next.on("stopped", () => {
        if (isStale()) return;
        isConnected = false;
        setConnectionId(null);
        teardownClient();
        failCycle("disconnected");
      });

      next.on("server-message", (e) => {
        if (isStale()) return;
        handleEvent(e.message?.data);
      });
      next.on("group-message", (e) => {
        if (isStale()) return;
        handleEvent(e.message?.data);
      });

      try {
        await next.start();
      } catch {
        starting = false;
        if (isStale()) return;
        teardownClient();
        failCycle("disconnected");
        return;
      }
      starting = false;
      // Backstop poll until the first `connected`/`disconnected` event lands.
      if (!isConnected && !isStale()) startPolling(POLL_FALLBACK_MS);
    };

    // Network came back / the user refocused the tab: the usual cause of a dead
    // socket, and the moment they most want live updates. Reset the backoff and
    // reconnect immediately instead of waiting out a 5-minute timer.
    const wake = () => {
      if (cancelled || isConnected || starting) return;
      if (typeof navigator !== "undefined" && navigator.onLine === false) return;
      const now = Date.now();
      if (now - lastWakeAt < WAKE_THROTTLE_MS) return;
      lastWakeAt = now;
      cycleFailures = 0;
      scheduleRetry(0);
    };

    const onVisibility = () => {
      if (document.visibilityState === "visible") wake();
    };

    window.addEventListener("online", wake);
    window.addEventListener("focus", wake);
    document.addEventListener("visibilitychange", onVisibility);

    const init = async () => {
      // Always hydrate the inbox first — works regardless of realtime state.
      await helpersRef.current.hydrateInbox();
      if (cancelled) return;
      // Poll from the start; `connected` slows it back down.
      startPolling(POLL_FALLBACK_MS);
      connect();
    };

    init();

    return () => {
      cancelled = true;
      window.removeEventListener("online", wake);
      window.removeEventListener("focus", wake);
      document.removeEventListener("visibilitychange", onVisibility);
      if (pollTimer) clearInterval(pollTimer);
      if (retryTimer) clearTimeout(retryTimer);
      if (graceTimer) clearTimeout(graceTimer);
      try {
        client?.stop();
      } catch {
        // ignore
      }
      client = null;
      setConnectionId(null);
    };
    // Re-run only when the signed-in user changes (login / logout / impersonate).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [email]);

  return (
    <RealtimeContext.Provider
      value={{ connectionId, status, subscribeChat, subscribeDeal, subscribeBoard }}
    >
      {children}
    </RealtimeContext.Provider>
  );
}

export default RealtimeProvider;
